import { useEffect, useRef, useState } from "react";
import "./SharedComponents/CelebrationModal.css";

/* ============================================================
   CelebrationModal.jsx
   Financial Literacy Game — Badge & Trophy Celebration Modal

   Props:
     isOpen    {bool}     — controls visibility
     onClose   {func}     — called when player dismisses
     type      {string}   — "badge" | "trophy" | "jewel"
     title     {string}   — achievement name, e.g. "Budget Master"
     description {string} — flavour text shown below the title
     icon      {string}   — emoji to display on the badge/trophy

   Usage example:
     <CelebrationModal
       isOpen={showModal}
       onClose={() => setShowModal(false)}
       type="badge"
       title="Budget Master"
       description="You balanced your first monthly budget without going into debt!"
       icon="🏅"
     />
   ============================================================ */

/* ---------- Confetti helpers ---------- */
const CONFETTI_COLORS = [
  "#f7c948", "#ff7eb3", "#7b6cf6", "#43d9ad",
  "#f97316", "#60a5fa", "#a78bfa", "#34d399",
];
const SHAPES = ["square", "circle", "rect"];

function randomBetween(min, max) {
  return Math.random() * (max - min) + min;
}

function spawnConfetti(canvas) {
  const ctx    = canvas.getContext("2d");
  const W      = canvas.width;
  const H      = canvas.height;
  const cx     = W / 2;
  const cy     = H / 2;
  const COUNT  = 90;
  const particles = [];

  for (let i = 0; i < COUNT; i++) {
    const angle  = randomBetween(0, Math.PI * 2);
    const speed  = randomBetween(2.5, 7.5);
    const size   = randomBetween(6, 14);
    const shape  = SHAPES[Math.floor(Math.random() * SHAPES.length)];
    const color  = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
    const delay  = randomBetween(0, 18);   // frames before particle activates

    particles.push({
      x: cx, y: cy,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - randomBetween(1, 3), // slight upward bias
      size,
      shape,
      color,
      rotation: randomBetween(0, Math.PI * 2),
      rotSpeed: randomBetween(-0.18, 0.18),
      alpha: 1,
      delay,
      active: false,
    });
  }

  let frame = 0;
  let rafId;

  function draw() {
    ctx.clearRect(0, 0, W, H);
    frame++;
    let allDone = true;

    for (const p of particles) {
      // Activate after delay
      if (!p.active) {
        if (frame >= p.delay) p.active = true;
        else continue;
      }

      // Physics
      p.vy  += 0.18;          // gravity
      p.vx  *= 0.99;          // air drag
      p.x   += p.vx;
      p.y   += p.vy;
      p.rotation += p.rotSpeed;
      p.alpha     -= 0.013;   // fade out

      if (p.alpha <= 0) continue;
      allDone = false;

      // Draw
      ctx.save();
      ctx.globalAlpha = Math.max(p.alpha, 0);
      ctx.fillStyle   = p.color;
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rotation);

      if (p.shape === "circle") {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (p.shape === "rect") {
        ctx.fillRect(-p.size, -p.size / 2, p.size * 2, p.size / 2);
      } else {
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size);
      }

      ctx.restore();
    }

    if (!allDone) {
      rafId = requestAnimationFrame(draw);
    } else {
      ctx.clearRect(0, 0, W, H);
    }
  }

  draw();

  // Return cleanup so we can cancel on unmount
  return () => cancelAnimationFrame(rafId);
}

/* ---------- Decorative star positions ---------- */
const STAR_POSITIONS = [
  { top: "12%", left: "8%",  size: 20, delay: "0.3s",  char: "✦" },
  { top: "18%", right: "9%", size: 16, delay: "0.5s",  char: "✦" },
  { top: "72%", left: "6%",  size: 14, delay: "0.4s",  char: "★" },
  { top: "68%", right: "7%", size: 18, delay: "0.6s",  char: "✦" },
  { top: "42%", left: "3%",  size: 12, delay: "0.7s",  char: "✧" },
  { top: "38%", right: "4%", size: 12, delay: "0.55s", char: "✧" },
];

/* ---------- Component ---------- */
export default function CelebrationModal({
  isOpen      = false,
  onClose     = () => {},
  type        = "badge",       // "badge" | "trophy" | "jewell"
  title       = "Achievement Unlocked!",
  description = "You've done something amazing.",
  imgSrc        = "",
  trophyID      = "",
}) {
  const canvasRef   = useRef(null);
  const cleanupRef  = useRef(null);
  const [visible, setVisible] = useState(false);

 // Trigger confetti whenever the modal opens
  useEffect(() => {
    if (!isOpen) { setVisible(false); return; }

    setVisible(true);

    // Give DOM one frame to mount before accessing canvas
    const t = setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Size canvas to its rendered container
      const rect    = canvas.parentElement.getBoundingClientRect();
      canvas.width  = rect.width;
      canvas.height = rect.height;

      // Clean up any previous animation
      if (cleanupRef.current) cleanupRef.current();

      // First burst immediately
      cleanupRef.current = spawnConfetti(canvas);

      // Second burst after a delay
      const secondBurst = setTimeout(() => {
        if (canvasRef.current) spawnConfetti(canvasRef.current);
      }, 1200);

      // Store cleanup for both
      const firstCleanup = cleanupRef.current;
      cleanupRef.current = () => {
        firstCleanup();
        clearTimeout(secondBurst);
      };

    }, 30);

    return () => {
      clearTimeout(t);
      if (cleanupRef.current) cleanupRef.current();
    };
  }, [isOpen]);

  if (!visible) return null;

  let animationClass = "";
  let idPosition = "";
  let typeLabel = "";
  switch (type){
    case "trophy":
      animationClass = "image-grow";
      idPosition = "celebrate-trophy-id-position font-weight-bold";
      typeLabel = "trophy";
      break;
    case "jewel":
      animationClass = "animate-jewel";
      idPosition = "celebrate-jewel-id-position font-weight-bold";
      typeLabel = "jewel";
      break;
    case "extraCredit":
      animationClass = "challenge-image animate";
      typeLabel = "Extra Credit";
      break;
    default: 
      animationClass = "spinPictureAround";
      break;
  } 
  
  return (
    <div
      className="celebration-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cel-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="celebration-modal">

        {/* Confetti canvas sits behind everything */}
        <canvas ref={canvasRef} className="confetti-canvas" aria-hidden="true" />

        {/* Decorative stars */}
        {STAR_POSITIONS.map((s, i) => (
          <span
            key={i}
            className="star"
            aria-hidden="true"
            style={{
              top:       s.top,
              left:      s.left,
              right:     s.right,
              fontSize:  s.size + "px",
              color:     i % 2 === 0 ? "#f7c948" : "#7b6cf6",
              animationDelay: s.delay,
            }}
          >
            {s.char}
          </span>
        ))}

        {/* Spinning badge or rocking trophy */}
        <div className="glow-ring">
          <div className = {animationClass} key={trophyID}>
            <img className="trophy_thumbnail_img" src={imgSrc} 
                              alt="trophy"/>
            <div className={idPosition}>{trophyID}</div>
          </div>
        </div>

        {/* Text */}
        <div className="celebration-content">
          <p className="celebration-label">{typeLabel}</p>
          <h2 className="celebration-title" id="cel-title">{title}</h2>
          <p className="celebration-desc">{description}</p>

          <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <button className="celebration-btn" onClick={onClose}>
                  Close
              </button>
          </div>
        </div>

      </div>
    </div>
  );
}
